import React, { useState, useRef, useEffect } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

type Option = {
  value: string;
  label: string;
};

type Props = {
  setSelectedAimag: React.Dispatch<React.SetStateAction<string>>;
  selectedAimag: string;
  setSelectedSum: React.Dispatch<React.SetStateAction<string>>;
  selectedSum: string;
  setSelectedBag: React.Dispatch<React.SetStateAction<string>>;
  selectedBag: string;
};

type CustomSelectProps = {
  label: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
};

const CustomSelect = ({ 
  label, 
  value, 
  options, 
  onChange, 
  disabled, 
  placeholder 
}: CustomSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  const selectedOption = options.find(o => o.value === value);

  return (
    <div className="flex flex-col gap-1 w-full relative group" ref={wrapperRef}>
      <p className="text-sm sm:text-base font-semibold text-gray-700 mb-1">{label}</p>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`
          flex items-center justify-between w-full h-[42px] px-3 bg-white border rounded-lg transition-all duration-200 shadow-sm
          ${disabled 
            ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-400' 
            : 'cursor-pointer hover:border-orange-400 border-gray-300'
          }
          ${isOpen ? 'border-orange-500 ring-2 ring-orange-100' : ''}
          max-w-[250px]
        `}
      >
        <span className={`text-sm truncate mr-2 ${!selectedOption ? 'text-gray-400' : 'text-gray-900 font-medium'}`}>
          {selectedOption ? selectedOption.label : placeholder || "Сонгох..."}
        </span>
        {isOpen ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 20 }} className="text-gray-500" />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 20 }} className="text-gray-500" />
        )}
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full max-w-[250px] mt-2 bg-white border border-gray-100 rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] overflow-hidden origin-top" style={{top: "100%"}}>
          <div className="max-h-[240px] overflow-y-auto py-2">
            {options.map((option) => {
               const isSelected = option.value === value;
               return (
                <div
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`
                    px-4 py-2.5 text-sm cursor-pointer transition-all duration-150 flex items-center
                    ${isSelected 
                      ? 'text-white bg-gradient-to-r from-[#f97316] to-[#ef4444]' 
                      : 'text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-[#f97316] hover:to-[#ef4444]'
                    }
                  `}
                >
                  <span className={`flex-1 ${isSelected ? 'font-medium' : ''}`}>{option.label}</span>
                </div>
               );
            })}
             {options.length === 0 && (
                <div className="px-4 py-3 text-center text-sm text-gray-400 italic">Сонголт алга</div>
             )}
          </div>
        </div>
      )}
    </div>
  );
};

export const SelectLocationDropdown = (props: Props) => {
  const {
    setSelectedAimag,
    selectedAimag,
    setSelectedSum,
    selectedSum,
    setSelectedBag,
    selectedBag,
  } = props;
  
  // 22 аймаг / Улаанбаатараас эхлэнэ – value = label (монгол нэр)
  const aimags: Option[] = [
    { value: "Улаанбаатар хот", label: "Улаанбаатар хот" },
    { value: "Архангай аймаг", label: "Архангай аймаг" },
    { value: "Баян-Өлгий аймаг", label: "Баян-Өлгий аймаг" },
    { value: "Баянхонгор аймаг", label: "Баянхонгор аймаг" },
    { value: "Булган аймаг", label: "Булган аймаг" },
    { value: "Говь-Алтай", label: "Говь-Алтай" },
    { value: "Говьсүмбэр аймаг", label: "Говьсүмбэр аймаг" },
    { value: "Дархан-Уул аймаг", label: "Дархан-Уул аймаг" },
    { value: "Дорноговь аймаг", label: "Дорноговь аймаг" },
    { value: "Дорнод аймаг", label: "Дорнод аймаг" },
    { value: "Дундговь аймаг", label: "Дундговь аймаг" },
    { value: "Завхан аймаг", label: "Завхан аймаг" },
    { value: "Орхон аймаг", label: "Орхон аймаг" },
    { value: "Өвөрхангай аймаг", label: "Өвөрхангай аймаг" },
    { value: "Өмнөговь аймаг", label: "Өмнөговь аймаг" },
    { value: "Сүхбаатар аймаг", label: "Сүхбаатар аймаг" },
    { value: "Сэлэнгэ аймаг", label: "Сэлэнгэ аймаг" },
    { value: "Төв аймаг", label: "Төв аймаг" },
    { value: "Увс аймаг", label: "Увс аймаг" },
    { value: "Ховд аймаг", label: "Ховд аймаг" },
    { value: "Хөвсгөл аймаг", label: "Хөвсгөл аймаг" },
    { value: "Хэнтий аймаг", label: "Хэнтий аймаг" },
  ];

  const sums: Record<string, Option[]> = {
    "Улаанбаатар хот": [
      { value: "Багануур", label: "Багануур" },
      { value: "Багахангай", label: "Багахангай" },
      { value: "Баянгол", label: "Баянгол" },
      { value: "Баянзүрх", label: "Баянзүрх" },
      { value: "Налайх", label: "Налайх" },
      { value: "Сонгинохайрхан", label: "Сонгинохайрхан" },
      { value: "Сүхбаатар", label: "Сүхбаатар" },
      { value: "Хан-Уул", label: "Хан-Уул" },
      { value: "Чингэлтэй", label: "Чингэлтэй" },
    ],
    "Архангай аймаг": [
      { value: "Батцэнгэл", label: "Батцэнгэл" },
      { value: "Булган", label: "Булган" },
      { value: "Жаргалант", label: "Жаргалант" },
      { value: "Ихтамир", label: "Ихтамир" },
      { value: "Өгийнуур", label: "Өгийнуур" },
      { value: "Өлзийт", label: "Өлзийт" },
      { value: "Өндөр-Улаан", label: "Өндөр-Улаан" },
      { value: "Тариат", label: "Тариат" },
      { value: "Төвшрүүлэх", label: "Төвшрүүлэх" },
      { value: "Хайрхан", label: "Хайрхан" },
      { value: "Хангай", label: "Хангай" },
      { value: "Хашаат", label: "Хашаат" },
      { value: "Хотонт", label: "Хотонт" },
      { value: "Цахир", label: "Цахир" },
      { value: "Цэнхэр", label: "Цэнхэр" },
      { value: "Цэцэрлэг", label: "Цэцэрлэг" },
      { value: "Чулуут", label: "Чулуут" },
      { value: "Эрдэнэбулган", label: "Эрдэнэбулган" },
      { value: "Эрдэнэмандал", label: "Эрдэнэмандал" },
    ],
    "Баян-Өлгий аймаг": [
      { value: "Алтай", label: "Алтай" },
      { value: "Алтанцөгц", label: "Алтанцөгц" },
      { value: "Баяннуур", label: "Баяннуур" },
      { value: "Бугат", label: "Бугат" },
      { value: "Булган", label: "Булган" },
      { value: "Буянт", label: "Буянт" },
      { value: "Дэлүүн", label: "Дэлүүн" },
      { value: "Ногооннуур", label: "Ногооннуур" },
      { value: "Сагсай", label: "Сагсай" },
      { value: "Толбо", label: "Толбо" },
      { value: "Улаанхус", label: "Улаанхус" },
      { value: "Цагааннуур", label: "Цагааннуур" },
      { value: "Цэнгэл", label: "Цэнгэл" },
    ],
    "Баянхонгор аймаг": [
      { value: "Баацагаан", label: "Баацагаан" },
      { value: "Баянбулаг", label: "Баянбулаг" },
      { value: "Баянговь", label: "Баянговь" },
      { value: "Баянлиг", label: "Баянлиг" },
      { value: "Баян-Овоо", label: "Баян-Овоо" },
      { value: "Баян-Өндөр", label: "Баян-Өндөр" },
      { value: "Баянхонгор", label: "Баянхонгор" },
      { value: "Баянцагаан", label: "Баянцагаан" },
      { value: "Богд", label: "Богд" },
      { value: "Бөмбөгөр", label: "Бөмбөгөр" },
      { value: "Бууцагаан", label: "Бууцагаан" },
      { value: "Галуут", label: "Галуут" },
      { value: "Гурванбулаг", label: "Гурванбулаг" },
      { value: "Жаргалант", label: "Жаргалант" },
      { value: "Жинст", label: "Жинст" },
      { value: "Заг", label: "Заг" },
      { value: "Өлзийт", label: "Өлзийт" },
      { value: "Хүрээмарал", label: "Хүрээмарал" },
      { value: "Шинэжинст", label: "Шинэжинст" },
      { value: "Эрдэнэцогт", label: "Эрдэнэцогт" },
    ],
    "Булган аймаг": [
      { value: "Баян-Агт", label: "Баян-Агт" },
      { value: "Баяннуур", label: "Баяннуур" },
      { value: "Бугат", label: "Бугат" },
      { value: "Бүрэгхангай", label: "Бүрэгхангай" },
      { value: "Гурванбулаг", label: "Гурванбулаг" },
      { value: "Дашинчилэн", label: "Дашинчилэн" },
      { value: "Могод", label: "Могод" },
      { value: "Орхон", label: "Орхон" },
      { value: "Рашаант", label: "Рашаант" },
      { value: "Сайхан", label: "Сайхан" },
      { value: "Сэлэнгэ", label: "Сэлэнгэ" },
      { value: "Тэшиг", label: "Тэшиг" },
      { value: "Хангал", label: "Хангал" },
      { value: "Хишиг-Өндөр", label: "Хишиг-Өндөр" },
      { value: "Хутаг-Өндөр", label: "Хутаг-Өндөр" },
    ],
    "Говь-Алтай": [
      { value: "Алтай", label: "Алтай" },
      { value: "Баян-Уул", label: "Баян-Уул" },
      { value: "Бигэр", label: "Бигэр" },
      { value: "Бугат", label: "Бугат" },
      { value: "Дарви", label: "Дарви" },
      { value: "Дэлгэр", label: "Дэлгэр" },
      { value: "Жаргалан", label: "Жаргалан" },
      { value: "Есөнбулаг", label: "Есөнбулаг" },
      { value: "Тайшир", label: "Тайшир" },
      { value: "Тонхил", label: "Тонхил" },
      { value: "Төгрөг", label: "Төгрөг" },
      { value: "Халиун", label: "Халиун" },
      { value: "Хөхморьт", label: "Хөхморьт" },
      { value: "Цогт", label: "Цогт" },
      { value: "Цээл", label: "Цээл" },
      { value: "Чандмань", label: "Чандмань" },
      { value: "Шарга", label: "Шарга" },
      { value: "Эрдэнэ", label: "Эрдэнэ" },
    ],
    "Говьсүмбэр аймаг": [
      { value: "Сүмбэр", label: "Сүмбэр" },
      { value: "Баянтал", label: "Баянтал" },
      { value: "Шивээговь", label: "Шивээговь" },
    ],
    "Дархан-Уул аймаг": [
      { value: "Дархан", label: "Дархан" },
      { value: "Орхон", label: "Орхон" },
      { value: "Хонгор", label: "Хонгор" },
      { value: "Шарын гол", label: "Шарын гол" },
    ],
    "Дорноговь аймаг": [
      { value: "Айраг", label: "Айраг" },
      { value: "Алтанширээ", label: "Алтанширээ" },
      { value: "Даланжаргалан", label: "Даланжаргалан" },
      { value: "Дэлгэрэх", label: "Дэлгэрэх" },
      { value: "Замын-Үүд", label: "Замын-Үүд" },
      { value: "Иххэт", label: "Иххэт" },
      { value: "Мандах", label: "Мандах" },
      { value: "Өргөн", label: "Өргөн" },
      { value: "Сайхандулаан", label: "Сайхандулаан" },
      { value: "Улаанбадрах", label: "Улаанбадрах" },
      { value: "Хатанбулаг", label: "Хатанбулаг" },
      { value: "Хөвсгөл", label: "Хөвсгөл" },
      { value: "Эрдэнэ", label: "Эрдэнэ" },
    ],
    "Дорнод аймаг": [
      { value: "Баяндун", label: "Баяндун" },
      { value: "Баянтүмэн", label: "Баянтүмэн" },
      { value: "Баян-Уул", label: "Баян-Уул" },
      { value: "Булган", label: "Булган" },
      { value: "Гурванзагал", label: "Гурванзагал" },
      { value: "Дашбалбар", label: "Дашбалбар" },
      { value: "Матад", label: "Матад" },
      { value: "Сэргэлэн", label: "Сэргэлэн" },
      { value: "Халхгол", label: "Халхгол" },
      { value: "Хөлөнбуйр", label: "Хөлөнбуйр" },
      { value: "Хэрлэн", label: "Хэрлэн" },
      { value: "Цагаан-Овоо", label: "Цагаан-Овоо" },
      { value: "Чойбалсан", label: "Чойбалсан" },
      { value: "Чулуунхороот", label: "Чулуунхороот" },
    ],
    "Дундговь аймаг": [
      { value: "Адаацаг", label: "Адаацаг" },
      { value: "Баянжаргалан", label: "Баянжаргалан" },
      { value: "Говь-Угтаал", label: "Говь-Угтаал" },
      { value: "Гурвансайхан", label: "Гурвансайхан" },
      { value: "Дэлгэрхангай", label: "Дэлгэрхангай" },
      { value: "Дэлгэрцогт", label: "Дэлгэрцогт" },
      { value: "Дэрэн", label: "Дэрэн" },
      { value: "Луус", label: "Луус" },
      { value: "Өлзийт", label: "Өлзийт" },
      { value: "Өндөршил", label: "Өндөршил" },
      { value: "Сайнцагаан", label: "Сайнцагаан" },
      { value: "Сайхан-Овоо", label: "Сайхан-Овоо" },
      { value: "Хулд", label: "Хулд" },
      { value: "Цагаандэлгэр", label: "Цагаандэлгэр" },
      { value: "Эрдэнэдалай", label: "Эрдэнэдалай" },
    ],
    "Завхан аймаг": [
      { value: "Алдархаан", label: "Алдархаан" },
      { value: "Асгат", label: "Асгат" },
      { value: "Баянтэс", label: "Баянтэс" },
      { value: "Баянхайрхан", label: "Баянхайрхан" },
      { value: "Дөрвөлжин", label: "Дөрвөлжин" },
      { value: "Завханмандал", label: "Завханмандал" },
      { value: "Идэр", label: "Идэр" },
      { value: "Их-Уул", label: "Их-Уул" },
      { value: "Нөмрөг", label: "Нөмрөг" },
      { value: "Отгон", label: "Отгон" },
      { value: "Сантмаргаз", label: "Сантмаргаз" },
      { value: "Сонгино", label: "Сонгино" },
      { value: "Тосонцэнгэл", label: "Тосонцэнгэл" },
      { value: "Түдэвтэй", label: "Түдэвтэй" },
      { value: "Тэлмэн", label: "Тэлмэн" },
      { value: "Тэс", label: "Тэс" },
      { value: "Ургамал", label: "Ургамал" },
      { value: "Цагаанхайрхан", label: "Цагаанхайрхан" },
      { value: "Цагаанчулуут", label: "Цагаанчулуут" },
      { value: "Цэцэн-Уул", label: "Цэцэн-Уул" },
      { value: "Шилүүстэй", label: "Шилүүстэй" },
      { value: "Эрдэнэхайрхан", label: "Эрдэнэхайрхан" },
      { value: "Яруу", label: "Яруу" },
    ],
    "Орхон аймаг": [
      { value: "Баян-Өндөр", label: "Баян-Өндөр" },
      { value: "Жаргалант", label: "Жаргалант" },
    ],
    "Өвөрхангай аймаг": [
      { value: "Баруунбаян-Улаан", label: "Баруунбаян-Улаан" },
      { value: "Бат-Өлзий", label: "Бат-Өлзий" },
      { value: "Баянгол", label: "Баянгол" },
      { value: "Баян-Өндөр", label: "Баян-Өндөр" },
      { value: "Богд", label: "Богд" },
      { value: "Бүрд", label: "Бүрд" },
      { value: "Гучин-Ус", label: "Гучин-Ус" },
      { value: "Есөнзүйл", label: "Есөнзүйл" },
      { value: "Зүүнбаян-Улаан", label: "Зүүнбаян-Улаан" },
      { value: "Нарийнтээл", label: "Нарийнтээл" },
      { value: "Өлзийт", label: "Өлзийт" },
      { value: "Сант", label: "Сант" },
      { value: "Тарагт", label: "Тарагт" },
      { value: "Төгрөг", label: "Төгрөг" },
      { value: "Уянга", label: "Уянга" },
      { value: "Хайрхандулаан", label: "Хайрхандулаан" },
      { value: "Хархорин", label: "Хархорин" },
      { value: "Хужирт", label: "Хужирт" },
    ],
    "Өмнөговь аймаг": [
      { value: "Баяндалай", label: "Баяндалай" },
      { value: "Баян-Овоо", label: "Баян-Овоо" },
      { value: "Булган", label: "Булган" },
      { value: "Гурвантэс", label: "Гурвантэс" },
      { value: "Мандал-Овоо", label: "Мандал-Овоо" },
      { value: "Манлай", label: "Манлай" },
      { value: "Ноён", label: "Ноён" },
      { value: "Номгон", label: "Номгон" },
      { value: "Сэврэй", label: "Сэврэй" },
      { value: "Ханбогд", label: "Ханбогд" },
      { value: "Ханхонгор", label: "Ханхонгор" },
      { value: "Хүрмэн", label: "Хүрмэн" },
      { value: "Цогт-Овоо", label: "Цогт-Овоо" },
      { value: "Цогтцэций", label: "Цогтцэций" },
    ],
    "Сүхбаатар аймаг": [
      { value: "Асгат", label: "Асгат" },
      { value: "Баяндэлгэр", label: "Баяндэлгэр" },
      { value: "Дарьганга", label: "Дарьганга" },
      { value: "Мөнххаан", label: "Мөнххаан" },
      { value: "Наран", label: "Наран" },
      { value: "Онгон", label: "Онгон" },
      { value: "Сүхбаатар", label: "Сүхбаатар" },
      { value: "Түвшинширээ", label: "Түвшинширээ" },
      { value: "Түмэнцогт", label: "Түмэнцогт" },
      { value: "Уулбаян", label: "Уулбаян" },
      { value: "Халзан", label: "Халзан" },
      { value: "Эрдэнэцагаан", label: "Эрдэнэцагаан" },
    ],
    "Сэлэнгэ аймаг": [
      { value: "Алтанбулаг", label: "Алтанбулаг" },
      { value: "Баруунбүрэн", label: "Баруунбүрэн" },
      { value: "Баянгол", label: "Баянгол" },
      { value: "Ерөө", label: "Ерөө" },
      { value: "Жавхлант", label: "Жавхлант" },
      { value: "Зүүнбүрэн", label: "Зүүнбүрэн" },
      { value: "Мандал", label: "Мандал" },
      { value: "Орхон", label: "Орхон" },
      { value: "Орхонтуул", label: "Орхонтуул" },
      { value: "Сайхан", label: "Сайхан" },
      { value: "Сант", label: "Сант" },
      { value: "Түшиг", label: "Түшиг" },
      { value: "Хушаат", label: "Хушаат" },
      { value: "Хүдэр", label: "Хүдэр" },
      { value: "Цагааннуур", label: "Цагааннуур" },
      { value: "Шаамар", label: "Шаамар" },
    ],
    "Төв аймаг": [
      { value: "Алтанбулаг", label: "Алтанбулаг" },
      { value: "Аргалант", label: "Аргалант" },
      { value: "Архуст", label: "Архуст" },
      { value: "Батсүмбэр", label: "Батсүмбэр" },
      { value: "Баян", label: "Баян" },
      { value: "Баяндэлгэр", label: "Баяндэлгэр" },
      { value: "Баянжаргалан", label: "Баянжаргалан" },
      { value: "Баян-Өнжүүл", label: "Баян-Өнжүүл" },
      { value: "Баянхангай", label: "Баянхангай" },
      { value: "Баянцагаан", label: "Баянцагаан" },
      { value: "Баянцогт", label: "Баянцогт" },
      { value: "Баянчандмань", label: "Баянчандмань" },
      { value: "Борнуур", label: "Борнуур" },
      { value: "Бүрэн", label: "Бүрэн" },
      { value: "Дэлгэрхаан", label: "Дэлгэрхаан" },
      { value: "Жаргалант", label: "Жаргалант" },
      { value: "Заамар", label: "Заамар" },
      { value: "Лүн", label: "Лүн" },
      { value: "Мөнгөнморьт", label: "Мөнгөнморьт" },
      { value: "Өндөрширээт", label: "Өндөрширээт" },
      { value: "Сүмбэр", label: "Сүмбэр" },
      { value: "Сэргэлэн", label: "Сэргэлэн" },
      { value: "Угтаалцайдам", label: "Угтаалцайдам" },
      { value: "Цээл", label: "Цээл" },
      { value: "Эрдэнэ", label: "Эрдэнэ" },
      { value: "Эрдэнэсант", label: "Эрдэнэсант" },
    ],
    "Увс аймаг": [
      { value: "Баруунтуруун", label: "Баруунтуруун" },
      { value: "Бөхмөрөн", label: "Бөхмөрөн" },
      { value: "Давст", label: "Давст" },
      { value: "Завхан", label: "Завхан" },
      { value: "Зүүнговь", label: "Зүүнговь" },
      { value: "Зүүнхангай", label: "Зүүнхангай" },
      { value: "Малчин", label: "Малчин" },
      { value: "Наранбулаг", label: "Наранбулаг" },
      { value: "Өлгий", label: "Өлгий" },
      { value: "Өмнөговь", label: "Өмнөговь" },
      { value: "Өндөрхангай", label: "Өндөрхангай" },
      { value: "Сагил", label: "Сагил" },
      { value: "Тариалан", label: "Тариалан" },
      { value: "Түргэн", label: "Түргэн" },
      { value: "Тэс", label: "Тэс" },
      { value: "Ховд", label: "Ховд" },
      { value: "Хяргас", label: "Хяргас" },
      { value: "Цагаанхайрхан", label: "Цагаанхайрхан" },
    ],
    "Ховд аймаг": [
      { value: "Алтай", label: "Алтай" },
      { value: "Булган", label: "Булган" },
      { value: "Буянт", label: "Буянт" },
      { value: "Дарви", label: "Дарви" },
      { value: "Дөргөн", label: "Дөргөн" },
      { value: "Дуут", label: "Дуут" },
      { value: "Жаргалант", label: "Жаргалант" },
      { value: "Зэрэг", label: "Зэрэг" },
      { value: "Манхан", label: "Манхан" },
      { value: "Мөнххайрхан", label: "Мөнххайрхан" },
      { value: "Мөст", label: "Мөст" },
      { value: "Мянгад", label: "Мянгад" },
      { value: "Үенч", label: "Үенч" },
      { value: "Ховд", label: "Ховд" },
      { value: "Цэцэг", label: "Цэцэг" },
      { value: "Чандмань", label: "Чандмань" },
      { value: "Эрдэнэбүрэн", label: "Эрдэнэбүрэн" },
    ],
    "Хөвсгөл аймаг": [
      { value: "Алаг-Эрдэнэ", label: "Алаг-Эрдэнэ" },
      { value: "Арбулаг", label: "Арбулаг" },
      { value: "Баянзүрх", label: "Баянзүрх" },
      { value: "Бүрэнтогтох", label: "Бүрэнтогтох" },
      { value: "Галт", label: "Галт" },
      { value: "Жаргалант", label: "Жаргалант" },
      { value: "Их-Уул", label: "Их-Уул" },
      { value: "Мөрөн", label: "Мөрөн" },
      { value: "Рэнчинлхүмбэ", label: "Рэнчинлхүмбэ" },
      { value: "Тариалан", label: "Тариалан" },
      { value: "Тосонцэнгэл", label: "Тосонцэнгэл" },
      { value: "Төмөрбулаг", label: "Төмөрбулаг" },
      { value: "Түнэл", label: "Түнэл" },
      { value: "Улаан-Уул", label: "Улаан-Уул" },
      { value: "Ханх", label: "Ханх" },
      { value: "Хатгал", label: "Хатгал" },
      { value: "Цагааннуур", label: "Цагааннуур" },
      { value: "Цагаан-Уул", label: "Цагаан-Уул" },
      { value: "Цагаан-Үүр", label: "Цагаан-Үүр" },
      { value: "Цэцэрлэг", label: "Цэцэрлэг" },
      { value: "Чандмань-Өндөр", label: "Чандмань-Өндөр" },
      { value: "Шинэ-Идэр", label: "Шинэ-Идэр" },
      { value: "Эрдэнэбулган", label: "Эрдэнэбулган" },
    ],
    "Хэнтий аймаг": [
      { value: "Батноров", label: "Батноров" },
      { value: "Батширээт", label: "Батширээт" },
      { value: "Баян-Адарга", label: "Баян-Адарга" },
      { value: "Баянмөнх", label: "Баянмөнх" },
      { value: "Баян-Овоо", label: "Баян-Овоо" },
      { value: "Баянхутаг", label: "Баянхутаг" },
      { value: "Биндэр", label: "Биндэр" },
      { value: "Бор-Өндөр", label: "Бор-Өндөр" },
      { value: "Галшар", label: "Галшар" },
      { value: "Дадал", label: "Дадал" },
      { value: "Дархан", label: "Дархан" },
      { value: "Дэлгэрхаан", label: "Дэлгэрхаан" },
      { value: "Жаргалтхаан", label: "Жаргалтхаан" },
      { value: "Мөрөн", label: "Мөрөн" },
      { value: "Норовлин", label: "Норовлин" },
      { value: "Өмнөдэлгэр", label: "Өмнөдэлгэр" },
      { value: "Хэрлэн", label: "Хэрлэн" },
      { value: "Цэнхэрмандал", label: "Цэнхэрмандал" },
    ],
  };

  return (
    <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <CustomSelect 
        label="Аймаг/Хот"
        value={selectedAimag}
        options={aimags}
        onChange={(val) => {
          setSelectedAimag(val);
          setSelectedSum("");
          setSelectedBag("");
        }}
        placeholder="Аймаг/Хот сонгох"
      />

      <CustomSelect 
        label="Сум/Дүүрэг"
        value={selectedSum}
        options={selectedAimag ? (sums[selectedAimag] || []) : []}
        onChange={(val) => {
          setSelectedSum(val);
          setSelectedBag("");
        }}
        disabled={!selectedAimag}
        placeholder="Сум/Дүүрэг сонгох"
      />

      <div className="flex flex-col gap-1 w-full max-w-[250px]">
        <p className="text-sm sm:text-base font-semibold text-gray-700 mb-1">Баг/Хороо</p>
        <input
          type="text"
          value={selectedBag}
          onChange={(e) => setSelectedBag(e.target.value)}
          disabled={!selectedSum}
          placeholder="Баг/Хороо бичих"
          className={`
            w-full h-[42px] px-3 bg-white border rounded-lg transition-all duration-200 shadow-sm text-sm
            ${!selectedSum ? "bg-gray-100 cursor-not-allowed border-gray-200 text-gray-400" : "border-gray-300 hover:border-orange-400 focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none"}
          `}
        />
      </div>
    </div>
  );
};
